import { KeyboardEvent as ReactKeyboardEvent, useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import MuiLink from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import Slide from '@mui/material/Slide';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Close from '@mui/icons-material/Close';
import Search from '@mui/icons-material/Search';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useGlobals } from 'src/shared/contexts/appContext';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { HelpCenterArticleListItem } from '../models/helpCenterArticleListItem';
import { HelpCenterSearchResult } from '../models/helpCenterSearchResult';
import { getHelpCenterArticlesByModule, searchHelpCenter } from '../services/helpCenterService';
import { getHelpModuleForPath } from '../functions/getHelpModuleForPath';
import { getSearchWords } from '../functions/highlightWords';
import { HelpPanelArticle } from './helpPanelArticle';
import { HelpPanelSearchResults } from './helpPanelSearchResults';
import { HelpPanelSuggestions } from './helpPanelSuggestions';

// Headerns höjd. Ett systemmeddelande ovanför headern gör den högre, som för sidomenyn.
const HEADER_HEIGHT = 50;
// Paus i skrivandet innan sökningen görs, som i hjälpcentrets sökfält.
const SEARCH_DELAY = 300;

// Den flytande hjälpknappen styr panelen (aria-controls) och får tillbaka fokus när panelen stängs.
export const HELP_PANEL_ID = 'help-panel';
export const HELP_BUTTON_ID = 'help-panel-button';

/**
 * Hjälppanelen till höger: artiklar för modulen som sidan hör till, sökning i hela hjälpcentret och artikeln man valt.
 * Den spärrar inte sidan bakom, så att man kan följa en guide medan man arbetar, och förblir öppen när man byter sida.
 */
export const HelpPanel = () => {
  const { t } = useTranslation(['help', 'helpArticles']);
  const { helpPanelOpen, setHelpPanelOpen, systemMessage } = useGlobals();
  const { pathname } = useLocation();
  const moduleKey = getHelpModuleForPath(pathname);

  // Svaret sparas med modulen det gäller. Listan visas bara när den hör till sidans modul, så att förra sidans förslag
  // inte syns under den nya rubriken medan nya hämtas.
  const [loaded, setLoaded] = useState<{ module: string; articles: HelpCenterArticleListItem[]; failed: boolean } | null>(null);
  const current = loaded && loaded.module === moduleKey ? loaded : null;
  const loading = !!moduleKey && !current;
  const articles = current?.articles ?? [];
  const failed = current?.failed ?? false;

  // Sökningen finns kvar när panelen stängs och öppnas igen, som artikeln. Under två tecken visas förslagen.
  const [query, setQuery] = useState<string>('');
  const [searched, setSearched] = useState<{ query: string; results: HelpCenterSearchResult[]; failed: boolean } | null>(null);
  // Orden som söks; ett extra mellanslag eller ett ord under två tecken ger inget nytt anrop.
  const searchWords = getSearchWords(query).join(' ');
  const searching = searchWords.length > 0;

  // Artikeln som är öppen i panelen. Den finns kvar när man byter sida och när panelen stängs och öppnas igen, så att
  // man kan fortsätta en guide; bara en omladdning glömmer den.
  const [openSlug, setOpenSlug] = useState<string | null>(null);
  // Sant när artikeln öppnats med ett klick i panelen: då får artikeln fokus. Öppnas panelen igen med en sparad artikel
  // får rubriken fokus direkt, så att fokus inte hoppar från sidan när artikeln laddats klart.
  const [focusArticle, setFocusArticle] = useState<boolean>(false);

  const openArticle = (slug: string) => {
    setOpenSlug(slug);
    setFocusArticle(true);
  };

  const backToList = () => {
    setOpenSlug(null);
    setFocusArticle(false);
  };

  // Fokus när en vy visas: sökfältet i listvyn (när panelen öppnas och vid Tillbaka), rubriken när panelen öppnas med en
  // sparad artikel. useCallback: en ny funktion varje rendering skulle flytta fokus varje gång.
  const focusOnMount = useCallback((element: HTMLElement | null) => element?.focus({ preventScroll: true }), []);
  const searchInput = useRef<HTMLInputElement | null>(null);
  const searchInputRef = useCallback(
    (element: HTMLInputElement | null) => {
      searchInput.current = element;
      focusOnMount(element);
    },
    [focusOnMount],
  );

  // Första Esc i fältet tömmer sökningen, nästa stänger panelen (preventDefault: panelens Esc-lyssnare hoppar över).
  const onSearchKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape' && query) {
      event.preventDefault();
      setQuery('');
    }
  };

  const clearSearch = () => {
    setQuery('');
    // X-knappen försvinner när fältet är tomt; fokus tillbaka till fältet istället för att tappas.
    searchInput.current?.focus();
  };

  // Länkarna navigerar bort, så där stannar fokus på den nya sidan.
  const close = () => setHelpPanelOpen(false);

  // X och Esc: fokus tillbaka till hjälpknappen, så att man fortsätter där man var. Knappen är dold medan panelen är
  // öppen; flushSync visar den innan den får fokus. På sidor utan knapp hamnar fokus på sidan när panelen glidit ut.
  const closeAndReturnFocus = useCallback(() => {
    flushSync(() => setHelpPanelOpen(false));
    document.getElementById(HELP_BUTTON_ID)?.focus();
  }, [setHelpPanelOpen]);

  // Stängd panel glömmer listan. Först när den glidit ut, så att fokus inte hoppar in i rubriken och ingen spinner
  // blinkar medan den försvinner.
  const resetAfterClose = () => {
    setLoaded(null);
    setFocusArticle(false);
  };

  // Hämtas när panelen öppnas och när modulen byts medan den är öppen. cancelled: ett sent svar för en sida man
  // redan lämnat skriver inte över den nya sidans förslag.
  useEffect(() => {
    if (!helpPanelOpen || !moduleKey) return undefined;

    let cancelled = false;
    const load = async () => {
      try {
        const result = await getHelpCenterArticlesByModule(moduleKey);
        if (!cancelled) setLoaded({ module: moduleKey, articles: result.data, failed: false });
      } catch (e) {
        ConsoleHelper.log(e);
        if (!cancelled) setLoaded({ module: moduleKey, articles: [], failed: true });
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [helpPanelOpen, moduleKey]);

  // Söker efter en paus i skrivandet. cancelled: bara den senaste sökningen räknas, så ett sent svar för en tidigare
  // bokstav, eller för det man suddat ut, fyller inte listan. De förra träffarna står kvar medan nya hämtas.
  useEffect(() => {
    if (!searching) {
      setSearched(null);
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const result = await searchHelpCenter(searchWords, { skipErrorToast: true });
        if (!cancelled) setSearched({ query: searchWords, results: result.data, failed: false });
      } catch (e) {
        ConsoleHelper.log(e);
        if (!cancelled) setSearched({ query: searchWords, results: [], failed: true });
      }
    }, SEARCH_DELAY);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchWords, searching]);

  useEffect(() => {
    if (!helpPanelOpen) return undefined;

    // defaultPrevented: Esc som en dialog, meny eller förslagslista redan tagit hand om stänger inte också panelen.
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) closeAndReturnFocus();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [helpPanelOpen, closeAndReturnFocus]);

  const top = HEADER_HEIGHT + (systemMessage?.height ?? 0);

  return (
    <Slide direction='left' in={helpPanelOpen} mountOnEnter unmountOnExit onExited={resetAfterClose}>
      <Paper
        id={HELP_PANEL_ID}
        component='aside'
        aria-label={t('help:panelTitle')}
        elevation={8}
        sx={{
          position: 'fixed',
          top,
          right: 0,
          bottom: 0,
          // Bred nog för artiklarnas skärmdumpar, men smalare på små skärmar så att sidan bakom fortfarande syns.
          width: { xs: '100%', sm: 400, md: 600 },
          zIndex: (theme) => theme.zIndex.appBar - 1,
          display: 'flex',
          flexDirection: 'column',
          borderRadius: { xs: 0, sm: '12px 0 0 0' },
          overflow: 'hidden',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5 }}>
          <Typography
            ref={openSlug && !focusArticle ? focusOnMount : undefined}
            tabIndex={-1}
            variant='h6'
            component='h2'
            sx={{ flexGrow: 1, fontWeight: 700, outline: 'none' }}
          >
            {t('help:panelTitle')}
          </Typography>
          <Tooltip title={t('help:panelClose')}>
            <IconButton onClick={closeAndReturnFocus} aria-label={t('help:panelClose')}>
              <Close fontSize='small' />
            </IconButton>
          </Tooltip>
        </Box>
        <Divider />

        {/* Sökfältet bara i listvyn; det får fokus när listan visas, så att man kan börja skriva direkt. */}
        {!openSlug && (
          <Box sx={{ px: 2, pt: 2 }}>
            <TextField
              inputRef={searchInputRef}
              fullWidth
              size='small'
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={onSearchKeyDown}
              placeholder={t('help:panelSearchPlaceholder')}
              InputProps={{
                inputProps: { 'aria-label': t('help:panelSearchPlaceholder') },
                startAdornment: (
                  <InputAdornment position='start'>
                    <Search fontSize='small' />
                  </InputAdornment>
                ),
                endAdornment: query ? (
                  <InputAdornment position='end'>
                    <Tooltip title={t('help:panelClearSearch')}>
                      <IconButton size='small' onClick={clearSearch} aria-label={t('help:panelClearSearch')}>
                        <Close fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </Box>
        )}

        {/* key: en ny ruta för varje vy, så att man börjar överst i artikeln och i listan. */}
        <Box key={openSlug ?? (searching ? 'search' : 'list')} sx={{ flexGrow: 1, overflowY: 'auto', px: 2, py: 2 }}>
          {openSlug && <HelpPanelArticle slug={openSlug} onBack={backToList} onNavigate={close} autoFocus={focusArticle} />}
          {!openSlug && searching && (
            <HelpPanelSearchResults
              query={searched?.query ?? query}
              results={searched?.results ?? []}
              loading={!searched}
              failed={searched?.failed ?? false}
              onOpen={openArticle}
            />
          )}
          {!openSlug && !searching && (
            <HelpPanelSuggestions moduleKey={moduleKey} articles={articles} loading={loading} failed={failed} onOpen={openArticle} />
          )}
        </Box>

        <Divider />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, py: 1.5 }}>
          <MuiLink component={Link} to={RoutesEnum.Help} onClick={close} underline='hover'>
            {t('help:panelToHelpCenter')}
          </MuiLink>
        </Box>
      </Paper>
    </Slide>
  );
};
